import { ArrowRight, CheckCircle, LayoutDashboard, Video } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApplicationStore, type ExistingApplication } from '../store/applicationStore';

interface LocationState {
  existingApplication?: ExistingApplication;
}

export function SuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as LocationState | null;
  const existingApplication = locationState?.existingApplication;

  const data = useApplicationStore((state) => state.data);
  const serverApplicationId = useApplicationStore((state) => state.serverApplicationId);
  const resetApplication = useApplicationStore((state) => state.resetApplication);

  // Redirect if no submitted application and no existing application from navigation
  useEffect(() => {
    if (data.status !== 'submitted' && !serverApplicationId && !existingApplication) {
      void navigate('/');
    }
  }, [data.status, serverApplicationId, existingApplication, navigate]);

  // Use existing application data if available, otherwise use store data
  const applicationNumber =
    existingApplication?.applicationNumber ?? data.applicationId ?? serverApplicationId ?? 'APP-UNKNOWN';
  const targetApplicationId = existingApplication?.applicationId ?? serverApplicationId;
  const dashboardUrl = targetApplicationId
    ? `/dashboard?applicationId=${encodeURIComponent(targetApplicationId)}`
    : '/dashboard';
  const videoUrl = targetApplicationId ? `/video?applicationId=${encodeURIComponent(targetApplicationId)}` : '/video';

  return (
    <div className='min-h-[80vh] flex flex-col items-center justify-center p-4 text-center bg-brand-pale'>
      <div className='bg-white p-8 rounded-2xl shadow-xl border border-gray-100 max-w-lg w-full'>
        <div className='bg-green-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-5'>
          <CheckCircle size={48} className='text-green-600' />
        </div>

        <h1 className='text-3xl md:text-4xl font-bold text-brand-gray mb-2'>{t('success.title')}</h1>
        <p className='text-gray-600 text-lg mb-6'>{t('success.subtitle')}</p>

        <div className='bg-brand-pale border border-blue-100 rounded-lg p-4 mb-6'>
          <p className='text-xs text-primary uppercase tracking-wide font-bold'>{t('success.applicationId')}</p>
          <p className='text-3xl font-mono font-bold text-brand-gray mt-1 tracking-wider'>{applicationNumber}</p>
        </div>

        <div className='flex flex-col gap-3'>
          <button
            type='button'
            onClick={() => void navigate(dashboardUrl)}
            className='flex items-center justify-center px-6 py-4 bg-brand-gray text-white text-xl font-bold rounded-lg hover:bg-black transition shadow-lg'>
            <LayoutDashboard size={20} className='mr-3' />
            {t('success.goToDashboard')}
          </button>

          <button
            type='button'
            onClick={() => void navigate(videoUrl)}
            className='flex items-center justify-center px-6 py-4 bg-white border-2 border-primary text-primary text-xl font-bold rounded-lg hover:bg-blue-50 transition'>
            <Video size={20} className='mr-3' />
            {t('success.startInterview')}
            <ArrowRight size={20} className='ml-2' />
          </button>

          <button
            type='button'
            onClick={() => {
              resetApplication();
              void navigate('/');
            }}
            className='text-sm text-gray-500 hover:text-gray-700 mt-2'>
            {t('success.backToHome')}
          </button>
        </div>
      </div>

      <p className='text-xs text-gray-400 mt-8 max-w-xs mx-auto'>{t('success.confirmationSent')}</p>
    </div>
  );
}
